import React, { ReactNode } from 'react';
import dayjs from 'dayjs';
import { Modal, FormInstance } from 'antd';
import copy from 'copy-text-to-clipboard';
import moment from 'moment-timezone';
import qs from 'qs';
import errorSound from 'assets/audios/error.mp3';
import { CopySpan, message,CopyIcon } from 'components/common';
import {
  COMMON_DIALOG_HEIGHT,
  DEFAULT_DATE_TIME_FORMAT,
  DEFAULT_ISO_TIME_FORMAT,
  DEFAULT_WAREHOUSE_CODE,
  DEFAULT_WAREHOUSE_ID,
  POPUP_WINDOW_LOCATION_PREFIX,
  PRINTED_LABEL_CACHE_LIST_LENGTH,
  LS_DEFAULT_WAREHOUSE_KEY,
} from 'constants/config';
import { siteCfg } from 'constants/sites/config';
import { fetchProfileSettings, fetchPermissions } from 'services/user';
import store from 'store';

const printedLabelList: StringKAnyVPair[] = [];
let profileSettings: StringKAnyVPair[] = [];
let permissions: StringKAnyVPair[] = [];

export const arrayBufferToJSON = (buf: ArrayBuffer) => {
  const decoder = new TextDecoder("utf-8");
  const jsonString = decoder.decode(buf);
  let ret: any = null;

  try {
    ret = JSON.parse(jsonString);
  } catch(e) {
    console.log(`Decoded ArrayBuffer error: ${e}`);
  }

  return ret;
};

/**
 * Sorter for booleans.
 * It can be used in [].sort function.
 */
export const boolSorter = (b1: boolean, b2: boolean) => {
  return b1 && !b2 ? 1 : -1;
};

/**
 * Check whether the shipment label had been printed.
 */
export const checkPrintedLabel = (shipmentNum: number) => {
  let found = false;
  let msg = '';

  for (let i = 0; i < printedLabelList.length; i++) {
    const item = printedLabelList[i];

    if (item.shipmentNum === shipmentNum) {
      found = true;
      //msg = `Shipment ${shipmentNum} has been printed at ${item.time}`;
      msg = 'Print job was submitted';
    }
  }

  //console.log('-------->', shipmentNum);
  printedLabelList.push({
    shipmentNum,
    time: convertTimeByUtc(moment().format()),
  });

  if (printedLabelList.length > PRINTED_LABEL_CACHE_LIST_LENGTH) {
    printedLabelList.splice(0, printedLabelList.length - PRINTED_LABEL_CACHE_LIST_LENGTH);
  }
  //console.log('--->', printedLabelList.length, printedLabelList);

  if (found) {
    message.warning(msg);
  }
};

/**
 * Convert UTC time to the time of a special time zone.
 *
 * @string time - the time need to convert, if it is an empty string, the function will return an empty string, the time can not be a timestamp number, because the library is based on the local time zone
 * @string tz - the time zone, if it is empty, it will use current time zone
 * @string inputFmt -  the date format of the input time
 * @string fmt - the format of the output time, if it is empty, it will use default time format
 * @return string
 */
export const convertTimeByUtc = (
  time: string,
  tz = '',
  inputFmt = '',
  fmt = '',
): string => {
  if (!time) return '';

  tz = tz || getSysTimeZone();
  fmt = fmt || DEFAULT_DATE_TIME_FORMAT;
  return moment.utc(time, inputFmt).tz(tz).format(fmt);
};

/**
 * convert a time from a time zone to another time zone.
 */
export const convertTimeByTimeZone = (
  src: StringKAnyVPair,
  target: StringKAnyVPair
): string => {
  let time = src.time || moment().format();
  let tz = src.tz || '';
  let inputFmt = src.inputFmt || '';
  const timeUtc = convertToUtcTime(time, tz, inputFmt);
  let fmt = target.fmt || '';

  tz = target.tz || '';
  inputFmt = target.inputFmt || '';

  return convertTimeByUtc(timeUtc, tz, inputFmt, fmt);
};

/**
 * Convert a time to UTC time.
 *
 * @string time - the time to convert, if it is empty, the function will return an empty string
 * @string tz - the time zone of the input time
 * @string inputFmt - the fomat of the input time
 * @string fmt - the format of the target time
 * @return string
 */
export const convertToUtcTime = (
  time: string,
  tz = '',
  inputFmt = '',
  fmt = ''
): string => {
  if (!time) return '';

  tz = tz || getSysTimeZone();
  fmt = fmt || DEFAULT_ISO_TIME_FORMAT;

  return moment.tz(time, inputFmt, tz).utc().format(fmt);
};

/**
 * Copy text content to clipboard.
 * @string text - the text need to be copied
 * @string msg - the message when copying is successful
 */
export const copyTextContent = (
  text: string,
  msg = '',
) => {
  copy(text);

  msg && message.info(msg);
};

export const customConsole = () => {
  const backup = console.error;

  console.error = function filterWarnings(msg: any, ...args: any[]) {
    if (msg !== undefined) {
      if (typeof msg === 'string') {
        const supressedProps = ['currentEditCompletePromise', 'gotoPage'];
        const supressedWarnings: string[] = [];
        const [second] = [...args];

        if (second && typeof second === 'string') {
          if (supressedProps.some(e => e.includes(second))) {
            return;
          }
        }

        //console.log('warning -->', msg, ...args);
        if (supressedWarnings.some(entry => msg.includes(entry))) {
          return;
        }

        backup.apply(console, [msg, ...args]);
      } else {
        backup.apply(console, [msg, ...args]);
      }
    }
  };
};

/**
 * Sorter for Date type.
 */
export const dateSorter = (d1: any, d2: any) => {
  //console.log('tt->', typeof d1, typeof d2, (new Date(d1)).getTime());
  return new Date(d1).getTime() >= new Date(d2).getTime() ? 1 : -1;
};

export const fetchSiteConfig = (hn: string = window.location.host) => {
  let d = siteCfg as StringKAnyVPair;
  let ret: StringKAnyVPair = { name: '' };

  if (d[hn] && typeof d[hn] === 'object') ret = d[hn];
  else {
    console.log('scfg', hn, d);
  }

  return ret as StringKAnyVPair;
};

/**
 * Get the dialog lex width for a general dialog.
 */
export const flexDialogWidth = (isFullscreen = false) => {
  const W = window.innerWidth;

  return isFullscreen
    ? W - 4
    : W > 1280
      ? W * (W > 1440 ? 0.8 : 0.92)
      : 1280;
};

/**
 * Get height for regular dialog.
 *
 * @param {?number} maxHeight
 * @return {number}
 */
export const getCommonDialogHeight = (maxHeight?: number): number => {
  let height = COMMON_DIALOG_HEIGHT;

  if (maxHeight) {
    if (maxHeight < window.innerHeight) {
      return maxHeight;
    } else {
      height = window.innerHeight;
    }
  }

  return window.innerHeight > height ? height : window.innerHeight;
};

/**
 * Get common grid minimal size.
 */
export const getCommonGridMinHeight = (offsetTop = 0): number => {
  return window.innerHeight - offsetTop;
};

/**
 */
export const getPopupWindowUrl = (path: string) => {
  return `${window.location.origin}${POPUP_WINDOW_LOCATION_PREFIX}/${path}`;
};

/**
 * Get a possible printer account.
 */
export const getPrinterAccountNumList = (): number[] => {
  const {
    sales: { packingPrinter, packingPrinter2, shipmentPrinter },
  } = store.getState() as StringKAnyVPair;
  const printers = [packingPrinter, packingPrinter2, shipmentPrinter];
  const ret: number[] = [];
  //console.log('printers', printers);

  for (let i = 0; i < printers.length; i++) {
    const printer = printers[i];

    if (printer && typeof printer === 'object' && printer.printerAccountNum) {
      if (ret.indexOf(printer.printerAccountNum) === -1) {
        ret.push(printer.printerAccountNum);
      }
    }
  }

  return ret;
};

/**
 * Get the profile value of a special profile setting.
 */
export const getProfileSettingValue = async (
  k: string,
  defaultValue: any = undefined,
) => {
  if (profileSettings.length === 0) {
    await getProfileSettings();
  }

  if (profileSettings.length > 0) {
    const ps = profileSettings.filter(e => e.settingCode === k);

    if (ps.length > 0) {
      return ps[0].settingValue || defaultValue;
    }
  }

  return defaultValue;
};

/**
 * Get profile settings.
 */
export const getProfileSettings = async () => {
  try {
    const settings = await fetchProfileSettings();

    if (Array.isArray(settings)) {
      profileSettings = settings;
    }
  } catch (e) {
    message.error(`Fetch profile setting error: ${e}`);
  }
};

/**
 * Parse the query string in URL.
 * The query string is like `[?#]foo=bar&other=thing`.
 *
 * @param {string} query - the query string
 * @return {{[key: string]: string}}
 */
export const getQueryStringParams = (query: string) => {
  return query
    ? (/^[?#]/.test(query) ? query.slice(1) : query)
      .split('&')
      .reduce((params: StringKVPair, param: string) => {
        const [key, value] = param.split('=');
        params[key] = value
          ? decodeURIComponent(value.replace(/\+/g, ' '))
          : '';
        return params;
      }, {})
    : {};
};

/**
 * Get the source shipping address of a shipment.
 */
export const getShippingFromAddress = (
  shipment: StringKAnyVPair,
  delimiter = ', '
) => {
  //console.log('get shipping addr', shipment);
  //let postal = shipment.shipToPostalCode;
  const street = [
    shipment.shipFromStreet1,
    shipment.shipFromStreet2,
    shipment.shipFromStreet3,
  ]
    .filter((e) => e)
    .join(', ');

  return [
    street,
    shipment.shipFromCity,
    `${shipment.shipFromState}`,
    shipment.shipFromCountry,
  ]
    .filter((e) => e)
    .join(delimiter);
};

/**
 * Get the target shipping address of a shipment.
 */
export const getShippingToAddress = (
  shipment: StringKAnyVPair,
  delimiter = ', '
) => {
  //console.log('get shipping addr', shipment);
  let postal = shipment.shipToPostalCode;
  const street = [
    shipment.shipToAddressLine1,
    shipment.shipToAddressLine2,
    shipment.shipToAddressLine3,
  ]
    .filter((e) => e)
    .join(', ');

  return [
    street,
    shipment.shipToCity,
    `${shipment.shipToState} ${postal}`,
    shipment.shipToCountry,
  ]
    .filter((e) => e)
    .join(delimiter);
  //return `${streets.join(', ')}, ${shipment.shipToCity}, ${shipment.shipToState} ${postal}, ${shipment.shipToCountry}`;
};

/**
 * Get system's IANA time zone.
 * This function needs modern browsers to support.
 */
export const getSysTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Get warehouse from cache data in redux.
 *
 * @param {number} id - the warehouse number
 * @return StringKAnyVPair | undefined
 */
export const getWarehouseFromCache = (
  id: number
): StringKAnyVPair | undefined => {
  const {
    admin: { warehouses },
  } = store.getState() as StringKAnyVPair;

  if (warehouses && Array.isArray(warehouses)) {
    for (let i = 0; i < warehouses.length; i++) {
      if (warehouses[i].warehouseNum === id) {
        return JSON.parse(JSON.stringify(warehouses[i])) as StringKAnyVPair;
      }
    }
  }
};

/**
 * Get the warehouse id from warehouse row.
 */
export const getWarehouseId = (warehouse: WarehouseRow) => {
  return warehouse.warehouseId || DEFAULT_WAREHOUSE_ID;
};

/**
 * Get warehouse id from cache data in redux.
 *
 * @param {number} warehouseNum - the warehouse number
 * @return StringKAnyVPair | undefined
 */
export const getWarehouseIdFromCache = (warehouseNum: number): string => {
  const {
    admin: { warehouses, databaseNum },
  } = store.getState() as StringKAnyVPair;

  if (warehouses && Array.isArray(warehouses)) {
    for (let i = 0; i < warehouses.length; i++) {
      if (
        warehouses[i].id === warehouseNum ||
        warehouses[i].warehouseNum === warehouseNum
      ) {
        return warehouses[i].warehouseId;
      }
    }
  }

  if(warehouseNum === 0) {
    return `${databaseNum}-0`
  }

  return DEFAULT_WAREHOUSE_ID;
};

export const getWarehouseCodeFromCache = (warehouseNum: number): string => {
  const {
    admin: { warehouses },
  } = store.getState() as StringKAnyVPair;

  if (warehouses && Array.isArray(warehouses)) {
    for (let i = 0; i < warehouses.length; i++) {
      if (
        warehouses[i].id === warehouseNum ||
        warehouses[i].warehouseNum === warehouseNum
      ) {
        return warehouses[i].warehouseCode || warehouses[i].code;
      }
    }
  }

  return DEFAULT_WAREHOUSE_CODE;
};

export const getWarehouseTypeFromCache = (warehouseNum: number): number => {
  const {
    admin: { warehouses },
  } = store.getState() as StringKAnyVPair;

  if (warehouses && Array.isArray(warehouses)) {
    for (let i = 0; i < warehouses.length; i++) {
      if (
        warehouses[i].id === warehouseNum ||
        warehouses[i].warehouseNum === warehouseNum
      ) {
        return warehouses[i].warehouseType;
      }
    }
  }

  return 0;
};

export const getVirtualWarehouseIdFromCache = (warehouseNum: number, warehouseType: number): string => {
  const {
    admin: { warehouses },
  } = store.getState() as StringKAnyVPair;

  if (warehouses && Array.isArray(warehouses)) {
    for (let i = 0; i < warehouses.length; i++) {
      if (
        warehouses[i].correspondingWarehouseNum === warehouseNum && warehouses[i].warehouseType === warehouseType
      ) {
        return warehouses[i].warehouseId
      }
    }
  }

  return DEFAULT_WAREHOUSE_CODE;
};

export const getVirtualWarehouseCodeFromCache = (warehouseNum: number, warehouseType: number): string => {
  const {
    admin: { warehouses },
  } = store.getState() as StringKAnyVPair;

  if (warehouses && Array.isArray(warehouses)) {
    for (let i = 0; i < warehouses.length; i++) {
      if (
        warehouses[i].correspondingWarehouseNum === warehouseNum && warehouses[i].warehouseType === warehouseType
      ) {
        return warehouses[i].warehouseCode
      }
    }
  }

  return DEFAULT_WAREHOUSE_CODE;
};
/**
 * Get the weight from LB and OZ.
 */
export const getWeightByLBOZ = (
  lb: number | undefined,
  oz: number | undefined
) => {
  const nlb: number = lb || 0;
  const noz: number = oz || 0;

  return nlb + noz * 0.0625;
};

/**
 * Get the weight LB and OZ.
 */
export const getWeightLBOZ = (weight: number) => {
  const lb = parseInt(`${weight}`);
  const oz = (weight - lb) * 16;

  return [lb, oz];
};

/**
 * Get the weight string.
 */
export const getWeightString = (weight: number) => {
  const [lb, oz] = getWeightLBOZ(weight);

  return `${lb} LB ${oz % 1 === 0 ? oz : oz.toFixed(2)} OZ`;
};

/**
 * Check whether a action is a BooleanAction.
 */
export const isBooleanAction = (action: any): action is BooleanAction => {
  const boolAction = action as BooleanAction;
  let ret = false;

  if (boolAction) {
    ret =
      'string' === typeof boolAction.type &&
      'boolean' === typeof boolAction.value;
  }

  return ret;
};

/**
 * Check whether a action is a BooleanAction.
 */
export const isDictAction = (action: any): action is DictAction => {
  const dictAction = action as DictAction;
  let ret = false;

  if (dictAction) {
    ret =
      'string' === typeof dictAction.type &&
      'object' === typeof dictAction.value;
  }

  return ret;
};

/**
 * Check whether a action has a number value.
 */
export const isNumberAction = (action: any): action is NumberAction => {
  let ret = false;

  if (action && 'object' === typeof action) {
    ret = 'string' === typeof action.type && 'number' === typeof action.value;
  }

  return ret;
};

/**
 * Check whether the os is Macos.
 */
export const isMacos = () => {
  return navigator.userAgent.indexOf('Mac OS X') > -1;
};

/**
 * Check whether a action has a string value.
 */
export const isStringAction = (action: any): action is StringAction => {
  let ret = false;

  if (action && 'object' === typeof action) {
    ret = 'string' === typeof action.type && 'string' === typeof action.value;
  }

  return ret;
};

/**
 * Image Url Verify.
 *
 * @param {string} url - the image url
 * @return {boolean}
 */
export const isImageUrl = (url: string): boolean => {
  if (typeof url !== 'string') return false;
  return url.match(/http[^]*.(jpg|jpeg|gif|png)(\?(.*))?$/gim) !== null;
};

/**
 * is testing env
 */
export const isTestEnv = () => {
  const devEnvs = ['development', 'local', 'testing', 'staging'];

  return devEnvs.indexOf(process.env.NODE_ENV || '') > -1;
}

export const makeJoinString = (
  list: Array<number | string>,
  delimiter: string = ';'
) => {
  return list.join(delimiter);
};

/**
 * Get current time of a special time zone.
 */
export const nowTime = (tz = '', fmt = ''): string => {
  //fmt = fmt || DEFAULT_DATE_TIME_FORMAT;
  tz = tz || getSysTimeZone();
  return moment.tz(moment().format(), '', tz).format(fmt);
};

/**
 * Play the Bell Sound.
 */
export const playBell = (bell: BellSoundType) => {
  const sound = bell === 'error' ? errorSound : `/audios/${bell}.mp3`;
  const audio = new Audio(sound);

  audio.play();
};

export const setPageTitle = (cfg = fetchSiteConfig()) => {
  const links = document.head.querySelectorAll('link');

  document.title = `${cfg.name}` || ' ';

  for (let i = 0; i < links.length; i++) {
    if (links[i].type === 'image/png' && cfg.logo) {
      //links[i].href = siteCfg.logo;
      links[i].setAttribute('href', cfg.logo);
    }
  }
};

/**
 * ant table sort by string
 * @param a
 * @param b
 * @param field
 * @returns
 */
export const sortByString = (a: any, b: any, field: string): number => {
  const strA = a[field] ? a[field].toUpperCase() : ''; // ignore upper and lowercase
  const strB = b[field] ? b[field].toUpperCase() : ''; // ignore upper and lowercase
  if (!strA || !strB) {
    return 0;
  }
  if (strA < strB) {
    return -1;
  }
  if (strA > strB) {
    return 1;
  }
  return 0;
};

/**
 * ant table sort by number
 * @param a
 * @param b
 * @param field
 * @returns
 */
export const sortByNumber = (a: any, b: any, field: string): number => {
  const numA = a[field];
  const numB = b[field];
  if (!numA || !numB) {
    return 0;
  }
  return numA - numB;
};

/**
 * ant table sort by date
 * @param a
 * @param b
 * @param field
 * @returns
 */
export const sortByDate = (a: any, b: any, field: string): number => {
  const dateA = a[field];
  const dateB = b[field];
  if (!dateA || !dateB) {
    return 0;
  }
  return dayjs(dateB).toDate().getTime() - dayjs(dateA).toDate().getTime();
};

/**
 * Split string into an array.
 */
export const splitStringToArray = (
  str: string,
  delimiter: string = ';'
): any[] => {
  return str.split(delimiter).filter((e) => e.trim());
};

/**
 * group statistic sku's quantity
 * @param items
 * @returns
 */
export const statItemsQuantity = (
  skus: Array<string>
): { [key: string]: number } => {
  // eslint-disable-next-line
  let values: { [key: string]: number } = {};
  skus.forEach((sku) => {
    if (sku) {
      values[sku] = (values[sku] || 0) + 1;
    }
  });
  return values;
};
/**
 * judge form content is changed
 * @param {[key: string]: any} snapshot
 * @param {FormInstance} form
 * @returns
 */
export const isFormChanged = (
  snapshot: StringKAnyVPair,
  form: FormInstance
): boolean => {
  const values = form.getFieldsValue();
  const keys = Object.keys(values);
  const changed = keys.find((key: string) => {
    const newValue = values[key] || '';
    const oldValue = snapshot[key] || '';
    if (moment.isMoment(oldValue) && moment.isMoment(newValue)) {
      const t1 = oldValue.toDate().getTime();
      const t2 = newValue.toDate().getTime();
      if (t1 === t2) {
        return null;
      }
    } else {
      if (newValue === oldValue) {
        return null;
      }
    }
    return key;
  })
    ? true
    : false;
  return changed;
};
/**
 * cancel edit form.if the content is changed, the user is prompted whether to cancel
 * @param {[key: string]: any} initValues
 * @param {FormInstance} form
 * @param {string} message
 * @returns {Promise<boolean>}
 */
export const leaveEditForm = (
  initValues?: StringKAnyVPair,
  form?: FormInstance,
  message?: string,
  theme?: Theme
): Promise<boolean> => {
  let changed = true;
  if (initValues && form) {
    changed = isFormChanged(initValues, form);
  }
  type TextProps = {
    children: string;
  };
  const Text = (props: TextProps) => {
    const { children } = props;
    return <div style={theme}>{children}</div>;
  };
  return new Promise((resolve) => {
    if (changed) {
      const tip = message || 'Changes you made may not be saved.';
      Modal.confirm({
        title: <Text>Leave / Refresh Page?</Text>,
        okText: 'Leave',
        cancelText: 'Cancel',
        content: <Text>{tip}</Text>,
        onOk: () => {
          resolve(true);
        },
        onCancel: () => {
          resolve(false);
        },
      });
    } else {
      resolve(true);
    }
  });
};
/**
 * error iamge
 * @returns
 */
export const getErrorImage = (): string => {
  return 'data:image/png;base64,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';
};

export const debounce = (fn: Function, delay: number): Function => {
  let timer: any = null;
  return function (this: any, ...args: any[]) {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      fn.apply(this, args);
      timer = null;
    }, delay);
  };
};

/**
 * parse url parameters
 * @param {string} url
 * @returns {{[key:string]:string}}
 */
export const getRouterParameters = (url?: string): StringKVPair => {
  let { search } = window.location;
  if (url) {
    const pos = url.indexOf('?');
    if (pos > 0) {
      search = url.substring(pos, url.length);
    } else {
      return {};
    }
  }
  return qs.parse(search.substring(1, search.length)) as any;
};
/**
 * Download CSV file
 * @param {any} res
 * @param {string} scope
 */
export const downloadCSV = (res: any, scope: string) => {
  //export file
  const blob = new Blob([res], { type: 'application/csv' });
  const name = `${scope}.csv`;
  if ('download' in document.createElement('a')) {
    // No IE Download
    const elink = document.createElement('a');
    elink.download = name;
    elink.style.display = 'none';
    elink.href = URL.createObjectURL(blob);
    document.body.appendChild(elink);
    elink.click();
    URL.revokeObjectURL(elink.href); // free url
    document.body.removeChild(elink);
  } else {
    // IE10+ download
    //navigator.msSaveBlob(blob, name);
  }
};

/**
 * Validate whether the http status code is a valid status.
 *
 * @param {Array<number>} codes - the valid status code list, do not need include 200, it will be included default.
 * @param {number} - the status code need to validated
 * @return {boolean}
 */
export const validateHttpStatus = (codes: Array<number>, status: number) => {
  const states = [200, ...codes];

  return states.indexOf(status) > -1;
};

/**
 * convert YYYY-MM-DDTHH:mm:00z to date
 * @param {string} str
 * @returns {Date}
 */
export function string2Date(str: string): Date {
  return dayjs(str, 'YYYY-MM-DDTHH:mm:00z').toDate();
}
/**
 * covert date to MM/DD/YYYY hh:mm:ssA
 * @param string | Date - the iso datetime string or the Date object
 * @param format
 * @returns {string}
 */
export function date2String(date: string | Date, format?: string): string {
  //format = format || 'MM/DD/YYYY hh:mm:ssA';
  format = format || DEFAULT_DATE_TIME_FORMAT;
  return dayjs(date).format(format);
}

/**
 * play Audio
 * @param {'success'|'error'} type
 * @returns {boolean}
 */
export function playAudio(type: 'success' | 'error'): void {
  const audio = new Audio(`/audios/${type}.mp3`);
  audio.play();
}

/**
 *
 */
export const byteConvert = function (bts: number) {
  if (Number.isNaN(bts)) {
    return '';
  }
  let bytes = bts;
  const symbols = ['bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  let exp = Math.floor(Math.log(bytes) / Math.log(2));
  if (exp < 1) {
    exp = 0;
  }
  const i = Math.floor(exp / 10);
  bytes /= 2 ** (10 * i);

  if (bytes.toString().length > bytes.toFixed(2).toString().length) {
    return `${bytes.toFixed(2)} ${symbols[i]}`;
  }
  return `${bytes} ${symbols[i]}`;
};

/**
 * return shipment title
 * @param {number} shipmentNum
 * @param {Array<OrderShipment>} shipments
 * @returns {string}
 */
export function getShipmentTitle(
  shipmentNum: number,
  shipments: Array<OrderShipment>
): string {
  const index = shipments.findIndex((row) => {
    if (row.shipmentNum === shipmentNum) {
      return true;
    }
    return false;
  });
  const title = `Shipment ${index + 1} of ${shipments.length}`;
  return title;
}
/**
 * Determine whether it is a smart device
 * @returns {boolean}
 */
export function isSmart(): boolean {
  return window.location.href.indexOf('/smart') > 0;
}
/**
 *  Determine whether it is the current page
 * @returns {boolean}
 */
export function isCurrentPage(page: string): boolean {
  return window.location.href.indexOf(page) > 0;
}

/**
 *  format money
 */
export function formatNum(num: string) {
  const value = Math.round(parseFloat(num) * 100) / 100;
  const arrayNum = value.toString().split('.');
  if (arrayNum.length === 1) {
    return value.toString() + '.00';
  }
  if (arrayNum.length > 1) {
    if (arrayNum[1].length < 2) {
      return value.toString() + '0';
    }
    return value;
  }
}

/**
 * isjsonString
 */
export function isJsonString(data: string) {
  try {
    if (typeof JSON.parse(data) === 'object') {
      return true;
    }
  } catch (error) { }
  return false;
}

/**
 *  trim form valus
 */
export function trimValues(data: any) {
  for (const key in data) {
    if (Object.prototype.hasOwnProperty.call(data, key)) {
      if (typeof data[key] === 'string') {
        data[key] = data[key].trim();
      }
    }
  }
  // setFieldsValue(...data)
  return data;
}

const getPermissions = async () => {
  try {
    const list = await fetchPermissions();

    if (Array.isArray(list)) {
      permissions = list;
    }
  } catch (e) {
    message.error(`Fetch permissions error: ${e}`);
  }
};

/**
 * Get the profile value of a special profile setting.
 */
export const getPermissionValue = async (
  k: number,
  defaultValue?: 0
) => {
  if (permissions.length === 0) {
    await getPermissions();
  }

  if (permissions.length > 0) {
    const ps = permissions.filter(e => e.permissionNum === k);

    if (ps.length > 0) {
      return ps[0].permissionLevel || defaultValue;
    }
  }

  return defaultValue;
};

/**
 * Format address
 */
export const formatAddress = (
  type: 'row' | 'col',
  pname = '',
  company = '',
  address1 = '',
  address2 = '',
  address3 = '',
  city = '',
  state = '',
  postCode = '',
  country = 'US',
) => {
  if (type === 'row') {
    return (
      <CopySpan value={`
        ${pname ? `${pname}, ` : ''}
        ${company ? `${company}, ` : ''}
        ${address1 ? `${address1}, ` : ''}
        ${address2 ? `${address2},` : ''}
        ${address3 ? `${address3},` : ''}
        ${city ? `${city}, ` : ''}
        ${state ? `${state}` : ''}
        ${postCode ? ` ${postCode}` : ''}${country ? `, ${country}` : ''}
        `}
      />
    )
  }
  else {
    return (
      <span>
        {pname && <div>{pname}</div>}
        {company && <div>{company}</div>}
        {address1 && <div>{address1}</div>}
        {address2 && <div>{address2}</div>}
        {address3 && <div>{address3}</div>}
        {(city || state || postCode) && <div>{`${city ? `${city}, ` : ''}${state ? `${state} ` : ''}${postCode}`}</div>}
        {country && <div>{country}</div>}
      </span>
    )
  }
}

export const shipToAddressIsPoBox = (shipment: OrderShipment) => {
  if (!shipment) return null;
  const address =
    `${shipment.shipToAddressLine1} ${shipment.shipToAddressLine2} ${shipment.shipToAddressLine3}`.toLowerCase();
  // eslint-disable-next-line
  const regex = new RegExp(/(?:P(?:ost(?:al)?)?[\.\-\s]*(?:(?:[O|0](?:ffice)?[\.\-\s]*)?B(?:ox|in|\b|\d)|[o|0](?:ffice|\b|\.)(?:[-\s]*\d)|code)|box[-\s\b]*\d)/i);
  const isMatched = regex.test(address);
  return isMatched;
};

export const onSelectionChange = (
  props: any,
  sourceList: any[],
  setSelected: Function,
  setSelectedRow: Function,
  id: string,
  isLocal?: boolean,
  setSourceList?: Function,
) => {
  // console.log(props)
  const { selected, data, unselected } = props;
  setSelected(selected);
  if (selected === true) {
    if (unselected) {
      const temp = sourceList.filter(
        (item: any) =>
          !Object.prototype.hasOwnProperty.call(unselected, `${item[id] === 0 ? 0 : item[id] || ''}`)
      );
      setSelectedRow(temp);
      // console.log(temp.length)
    } else {
      if (Array.isArray(data)) {
        setSelectedRow(data as any);
        if (isLocal) {
          setSourceList && setSourceList(data);
        }
        // console.log(data.length)
      } else {
        setSelectedRow(sourceList);
        // console.log(sourceList.length)
      }
    }
  }
  if (selected && selected !== true) {
    setSelectedRow(Object.values(selected as any));
    // console.log(Object.values(selected as any).length)
  }
  if (!selected) {
    if (unselected) {
      const temp = sourceList.filter(
        (item: any) =>
          !Object.prototype.hasOwnProperty.call(unselected, `${item[id] === 0 ? 0 : item[id] || ''}`)
      );
      setSelectedRow(temp);
      // console.log(temp.length)
    } else {
      setSelectedRow(Object.values(selected as any));
      // console.log(Object.values(selected as any).length)
    }
  }
};


export const checkSqlmintime = (time: string) => {
  if (!time) return true;
  return moment(time).diff(moment('1900/01/01', 'YYYY/MM/DD')) <= 0 // Not shown before 1900
}


export const formatPrinterToTree = (printers: Printer[]) => {
  const tree: any[] = [];
  const pcNameList: string[] = [];
  printers.map((item: Printer) => {
    if (item.deleted === 1) return true;
    const i = pcNameList.indexOf(item.pcName);
    if (i < 0) {
      pcNameList.push(item.pcName);
      tree.push({
        label: item.pcName,
        online: item.online ? true : false,
        options: [
          {
            label: item.printerName,
            value: item.printerNum,
          },
        ],
      });
    } else {
      if (item.online) { tree[i].online = true; }
      tree[i].options.push({
        label: item.printerName,
        value: item.printerNum,
        online: item.online ? true : false,
      });
    }
    return true;
  });
  return tree
}

export const formatToInch = (num?: number, type?: string) => {
  if (!num || !type) return 0;
  switch (type) {
    case 'Inch':
      return num;
    case 'Feet':
      return num * 12;
    case 'Centimeter':
      return num * 0.3937007874;
    case 'Meter':
      return num * 39.3700787402;
    case 'Millimeter':
      return num * 0.0393700787;
    default:
      return num;
  }
};

// export const WEIGHT_UNITS = ['Pound', 'Ounce', 'Kilogram', 'Gram'];
export const formatToPound = (num?: number, type?: string) => {
  if (!num || !type) return 0;
  switch (type) {
    case 'Pound':
      return num;
    case 'Ounce':
      return num * 0.0625;
    case 'Kilogram':
      return num * 2.20462262;
    case 'Gram':
      return num * 0.00220462262;
    default:
      return num;
  }
};

export const getLocaleTimezoneOffset = () => {
  const minOffset = new Date().getTimezoneOffset();
  const hour = Math.abs(Math.floor(minOffset / 60)).toString().padStart(2, '0');;
  const min = Math.abs(minOffset % 60).toString().padStart(2, '0');

  return `${minOffset > 0 ? '-' : '+'}${hour}:${min}`;
}


export const formatMessagewithBr = (str: string) => {
  if (str) {
    if (str.indexOf('\n') > -1) {
      const temp = str.split('\n');
      const comp: ReactNode = (
        <span>
          <CopyIcon value={str} />
          {temp.map((i) => (
            <span>
              {i}
              <br />
            </span>
          ))}
        </span>
      );
      return comp;
    } else {
      return <span> <CopyIcon value={str} />{str}</span>;
    }
  } else {
    return '';
  }
};

export const htmlToText = (htmlString: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');

  return doc.body.textContent || '';
};

export const isHTML = (str: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(str, "text/html");
  // If parsing produced any actual element nodes, it's HTML
  return Array.from(doc.body.childNodes).some(node => node.nodeType === 1);
};

export const randomString = (length = 16) => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const charactersLength = characters.length;
  let result = '';

  // eslint-disable-next-line
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }

  return result;
};

/**
 * sort warehouse - default warehouse set the zero
 */
export const sortWarehouse = (whs: WarehouseRow[]) => {
  const defaultWarehouseNum = Number(
    localStorage.getItem(LS_DEFAULT_WAREHOUSE_KEY)
  );
  const t = whs.filter((i)=> i.warehouseNum === defaultWarehouseNum)
  const s = whs.filter((i)=> i.warehouseNum !== defaultWarehouseNum)
  const sortArr = s.sort((a:any,b:any)=> sortByString(a, b, 'warehouseCode'));
  return [...t,...sortArr]
}

export const showModalMessage = (
  typ: 'info' | 'success' | 'error' | 'warning' | 'confirm',
  msg: string | StringKAnyVPair,
) => {
  const content = typeof msg === 'object' && msg.content ? msg.content : (typeof msg === 'string' ? msg : '');
  const title = typeof msg === 'object' && msg.title ? msg.title : typ;
  const opt = {
    ...(msg && typeof msg === 'object' ? msg : {}),
    content,
    title,
  };

  Modal[typ](opt);
};
